import React, { useState, useEffect } from 'react';
import api from './api';

const HRMaster = () => {
    // Tab State
    const [activeTab, setActiveTab] = useState('departments');

    // Data States
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [processRates, setProcessRates] = useState([]);

    // Form States
    const [deptName, setDeptName] = useState('');
    const [desigName, setDesigName] = useState('');
    const [processForm, setProcessForm] = useState({ processName: '', departmentId: '', defaultRate: '' });

    useEffect(() => {
        fetchDepartments();
        fetchDesignations();
        fetchProcessRates();
    }, []);

    // --- FETCH DATA ---
    const fetchDepartments = async () => {
        try {
            const res = await api.get('/hr-masters/departments');
            setDepartments(res.data.data || res.data || []);
        } catch (err) { console.error("Error fetching departments", err); }
    };

    const fetchDesignations = async () => {
        try {
            const res = await api.get('/hr-masters/designations');
            setDesignations(res.data.data || res.data || []);
        } catch (err) { console.error("Error fetching designations", err); }
    };

    const fetchProcessRates = async () => {
        try {
            const res = await api.get('/hr-masters/process-rates');
            setProcessRates(res.data.data || res.data || []);
        } catch (err) { console.error("Error fetching process rates", err); }
    };

    // --- SUBMIT HANDLERS ---
    const handleAddDepartment = async (e) => {
        e.preventDefault();
        try {
            await api.post('/hr-masters/departments', { name: deptName });
            setDeptName('');
            fetchDepartments();
        } catch (err) { alert("Error adding Department"); }
    };

    const handleAddDesignation = async (e) => {
        e.preventDefault();
        try {
            await api.post('/hr-masters/designations', { name: desigName });
            setDesigName('');
            fetchDesignations();
        } catch (err) { alert("Error adding Designation"); }
    };

    const handleAddProcessRate = async (e) => {
        e.preventDefault();
        try {
            await api.post('/hr-masters/process-rates', processForm);
            setProcessForm({ processName: '', departmentId: '', defaultRate: '' });
            fetchProcessRates();
        } catch (err) { alert("Error adding Process Rate"); }
    };

    // --- GROUP PROCESS RATES BY DEPARTMENT ---
    const groupedProcessRates = processRates.reduce((groups, rate) => {
        const deptName = rate.departmentId ? rate.departmentId.name : 'Unassigned';
        if (!groups[deptName]) {
            groups[deptName] = [];
        }
        groups[deptName].push(rate);
        return groups;
    }, {});

    return (
        <div className="container mt-4 mb-5">
            <h3 className="mb-4 fw-bold text-primary">⚙️ HR & Factory Masters</h3>

            {/* Navigation Tabs */}
            <ul className="nav nav-tabs mb-4">
                <li className="nav-item">
                    <button className={`nav-link fw-bold ${activeTab === 'departments' ? 'active bg-primary text-white' : ''}`} onClick={() => setActiveTab('departments')}>
                        🏢 Departments
                    </button>
                </li>
                <li className="nav-item">
                    <button className={`nav-link fw-bold ${activeTab === 'designations' ? 'active bg-primary text-white' : ''}`} onClick={() => setActiveTab('designations')}>
                        👔 Designations
                    </button>
                </li>
                <li className="nav-item">
                    <button className={`nav-link fw-bold ${activeTab === 'process' ? 'active bg-primary text-white' : ''}`} onClick={() => setActiveTab('process')}>
                        💎 Piece Rates / Processes
                    </button>
                </li>
            </ul>

            {/* TAB CONTENT */}
            <div className="row">
                
                {/* --- DEPARTMENTS TAB --- */}
                {activeTab === 'departments' && (
                    <>
                        <div className="col-md-4 mb-4">
                            <div className="card shadow-sm">
                                <div className="card-header bg-dark text-white fw-bold">➕ Add Department</div>
                                <div className="card-body">
                                    <form onSubmit={handleAddDepartment}>
                                        <input type="text" className="form-control mb-3" placeholder="e.g. AutoBottom" value={deptName} onChange={(e) => setDeptName(e.target.value)} required />
                                        <button className="btn btn-success w-100 fw-bold">Save</button>
                                    </form>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-8">
                            <table className="table table-hover shadow-sm bg-white">
                                <thead className="table-dark"><tr><th>Department Name</th><th>Status</th></tr></thead>
                                <tbody>
                                    {departments.map(d => (
                                        <tr key={d._id}>
                                            <td className="fw-bold">{d.name}</td>
                                            <td><span className="badge bg-success">Active</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {/* --- DESIGNATIONS TAB --- */}
                {activeTab === 'designations' && (
                    <>
                        <div className="col-md-4 mb-4">
                            <div className="card shadow-sm">
                                <div className="card-header bg-dark text-white fw-bold">➕ Add Designation</div>
                                <div className="card-body">
                                    <form onSubmit={handleAddDesignation}>
                                        <input type="text" className="form-control mb-3" placeholder="e.g. Polisher" value={desigName} onChange={(e) => setDesigName(e.target.value)} required />
                                        <button className="btn btn-success w-100 fw-bold">Save</button>
                                    </form>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-8">
                            <table className="table table-hover shadow-sm bg-white">
                                <thead className="table-dark"><tr><th>Designation Name</th><th>Status</th></tr></thead>
                                <tbody>
                                    {designations.map(d => (
                                        <tr key={d._id}>
                                            <td className="fw-bold">{d.name}</td>
                                            <td><span className="badge bg-success">Active</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {/* --- PROCESS RATES TAB --- */}
                {activeTab === 'process' && (
                    <>
                        <div className="col-md-4 mb-4">
                            <div className="card shadow-sm">
                                <div className="card-header bg-dark text-white fw-bold">➕ Add Piece Rate</div>
                                <div className="card-body">
                                    <form onSubmit={handleAddProcessRate}>
                                        <select className="form-select mb-3" value={processForm.departmentId} onChange={(e) => setProcessForm({...processForm, departmentId: e.target.value})} required>
                                            <option value="">-- Select Department --</option>
                                            {departments.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                                        </select>
                                        <input type="text" className="form-control mb-3" placeholder="Process Name (e.g. P1, AutoCut)" value={processForm.processName} onChange={(e) => setProcessForm({...processForm, processName: e.target.value})} required />
                                        <div className="input-group mb-3">
                                            <span className="input-group-text">₹</span>
                                            <input type="number" step="0.01" className="form-control" placeholder="Default Rate" value={processForm.defaultRate} onChange={(e) => setProcessForm({...processForm, defaultRate: e.target.value})} required />
                                        </div>
                                        <button className="btn btn-success w-100 fw-bold">Save</button>
                                    </form>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-8">
                            <table className="table table-hover shadow-sm bg-white">
                                <thead className="table-dark"><tr><th>Process / Size</th><th>Department</th><th>Rate (₹)</th></tr></thead>
                                <tbody>
                                    {Object.keys(groupedProcessRates).length === 0 ? (
                                        <tr><td colSpan="3" className="text-center text-muted py-4">No piece rates added yet.</td></tr>
                                    ) : (
                                        Object.keys(groupedProcessRates).sort().map(dept => (
                                            <React.Fragment key={dept}>
                                                {/* 📁 The Department Group Header */}
                                                <tr className="table-secondary border-dark">
                                                    <td colSpan="3" className="fw-bold text-dark text-uppercase">
                                                        📁 {dept}
                                                    </td>
                                                </tr>
                                                
                                                {/* 💎 The Individual Rates under this Department */}
                                                {groupedProcessRates[dept].map(p => (
                                                    <tr key={p._id}>
                                                        <td className="fw-bold text-primary ps-4">↳ {p.processName}</td>
                                                        <td className="text-muted">{dept}</td>
                                                        <td className="fw-bold text-success">₹ {Number(p.defaultRate).toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

            </div>
        </div>
    );
};

export default HRMaster;